export interface CuratedPalette {
  name: string;
  colors: string;
  note: string;
  surface: string;
  text: string;
  accent: string;
}

export interface PaletteCollection {
  slug: string;
  title: string;
  description: string;
  answer: string;
  updated: string;
  tips: string[];
  palettes: CuratedPalette[];
}

// Editorially selected connections, not claims that the exact named swatch is
// present in a collection. Each collection publishes its own explicit values.
export const COLOR_COLLECTION_LINKS: Record<string, { slug: string; reason: string }> = {
  E6E6FA: { slug: 'pastel', reason: 'For lavender-like surfaces, see a pastel newsletter form that keeps the soft fills and adds a readable label and a stronger input boundary.' },
  C8A2C8: { slug: 'pastel', reason: 'For a lilac-led interface, inspect the lilac, peach and aubergine palette, then use the worked form to check field boundaries separately from text.' },
  FFC0CB: { slug: 'pastel', reason: 'For pink surfaces, inspect the blush palette and a worked form that keeps a pale action fill while using a dark label and a checked field outline.' },
  BDFCC9: { slug: 'pastel', reason: 'The worked pastel form shows why a light mint field outline needs a darker boundary even when the surrounding pastel fills look distinct.' },
  A52A2A: { slug: 'earth-tone', reason: 'For brown and clay accents, inspect a terracotta product action whose white label is checked independently from brown reading text on a pale surface.' },
  D2691E: { slug: 'earth-tone', reason: 'For warm orange-brown actions, see the terracotta product example with a darker computed fill that preserves a white label.' },
  '9CAF88': { slug: 'earth-tone', reason: 'For muted green schemes, inspect the forest, moss and linen palette and keep its reading-text role separate from decorative moss fills.' },
  '808080': { slug: 'neutral', reason: 'For secondary text, see a neutral catalog example that darkens a faint supporting label while retaining hierarchy through spacing and type.' },
  A9A9A9: { slug: 'neutral', reason: 'For light gray surfaces and dividers, inspect the neutral collection before assigning one of those pale swatches to informative secondary text.' },
  F5F5DC: { slug: 'neutral', reason: 'For warm beige surfaces, see the linen catalog example with independently checked reading text, secondary text and action labels.' },
  '000080': { slug: 'dark-mode', reason: 'For navy-led interfaces, see a dashboard example that checks light text on two dark surfaces and both sides of an interactive card boundary.' },
  '36454F': { slug: 'dark-mode', reason: 'For charcoal-led interfaces, inspect the charcoal and amber palette and the worked dashboard example before relying on subtle surface differences.' },
  '800080': { slug: 'dark-mode', reason: 'For a purple-led dark theme, inspect the plum and lavender palette and the dashboard method for checking component text and adjacent boundaries.' },
};

export const PALETTE_COLLECTIONS: PaletteCollection[] = [
  {
    slug: 'pastel',
    title: 'Pastel Color Palettes',
    description: 'Three pastel palettes with HEX codes and a worked newsletter form: soft fills, readable labels, a corrected input border, and checked CSS roles.',
    answer: 'A pastel color palette combines light, softened colors such as blush pink, mint, lavender and pale blue. For a website, use these shades as surfaces or decorative areas and pair them with a darker reading color. These three five-color examples keep the pastel character while giving text a clearly assigned role.',
    updated: '2026-09-14',
    tips: [
      'Choose one pastel for the largest area; keep the others for illustrations, section backgrounds or highlights.',
      'Use the assigned dark text on the light surface. Two attractive pastels can still make an unreadable text pair.',
      'When using a pale accent for a button, check both its label and the boundary against its surroundings.'
    ],
    palettes: [
      {
        name: 'Blush, mint and plum',
        colors: 'FFF7FA-F3C9DA-CBE8DD-DFD5EF-382B43',
        surface: '#FFF7FA',
        text: '#382B43',
        accent: '#F3C9DA',
        note: 'Use the near-white pink for a page surface and deep plum for body copy. Blush can fill a featured card or primary action, while mint and lavender separate supporting illustrations. This combination suits a gentle editorial layout: keep long paragraphs on the near-white surface and give the other pastels enough space to remain distinct.'
      },
      {
        name: 'Sky, butter and midnight',
        colors: 'F4F9FF-C7DFF5-FFF0B3-D6E9E2-233449',
        surface: '#F4F9FF',
        text: '#233449',
        accent: '#C7DFF5',
        note: 'Start with pale blue-white behind the main content and midnight blue for reading text. The sky accent can introduce a selected card, while butter yellow draws attention to a small illustration or callout. Keep yellow away from white text; its brightness is more useful as a background than as a reading color.'
      },
      {
        name: 'Lilac, peach and aubergine',
        colors: 'FBF8FF-E0D3F5-F8DCC7-D4E3F4-3A2948',
        surface: '#FBF8FF',
        text: '#3A2948',
        accent: '#E0D3F5',
        note: 'Give a portfolio or invitation layout a lilac accent, with peach used sparingly to vary the temperature. Aubergine anchors headings and paragraphs against the almost-white violet surface. The pale blue is a supporting swatch for nonessential decoration; check a fresh pair before turning any of the softer colors into text, borders or chart labels.'
      }
    ]
  },
  {
    slug: 'earth-tone',
    title: 'Earth Tone Color Palettes',
    description: 'Earth tone palettes with terracotta, olive, sand and brown HEX codes, plus a worked product card with a corrected action color and checked CSS.',
    answer: 'An earth tone color palette uses colors reminiscent of soil, clay, stone and plants: brown, terracotta, sand, olive and muted green. For a usable digital scheme, combine a light neutral surface, a dark reading color and a more saturated accent. These examples show three different balances of warm and cool earthy colors.',
    updated: '2026-09-14',
    tips: [
      'Decide whether clay warmth or forest green should lead, then use the other family as a smaller counterpoint.',
      'Keep sand and stone tones for large surfaces when darker browns carry the body text.',
      'Use these HEX values for screen design. A print or paint match requires its own material and color workflow.'
    ],
    palettes: [
      {
        name: 'Terracotta and sandstone',
        colors: 'F7F0E6-C46B4D-D9B99B-827A52-3D3027',
        surface: '#F7F0E6',
        text: '#3D3027',
        accent: '#C46B4D',
        note: 'Place dark brown text on the warm off-white surface and reserve terracotta for a primary action or a short featured panel. Sandstone bridges those two extremes, while muted olive introduces a cooler note. For a ceramics shop or illustrated recipe page, give product photography a neutral surround so the clay accent does not compete with every image.'
      },
      {
        name: 'Forest, moss and linen',
        colors: 'F2F0E5-3D5A40-94A17A-C4AB87-29352A',
        surface: '#F2F0E5',
        text: '#29352A',
        accent: '#3D5A40',
        note: 'Linen forms the page background, deep green carries paragraphs and forest green fills the accent action. Moss and tan can support illustrations or category cards. This is a green-led alternative to a rust-heavy earth palette; retain the linen space between dark sections so the reading hierarchy stays clear without relying on hue alone.'
      },
      {
        name: 'Desert, rust and slate',
        colors: 'FAF3E8-A64932-D6B98C-72868A-343B3D',
        surface: '#FAF3E8',
        text: '#343B3D',
        accent: '#A64932',
        note: 'Use charcoal slate for text over pale sand, with rust as the main action color. The muted blue-gray provides a cooler supporting color beside the golden sand swatch. In a travel journal or landscape portfolio, use rust for a few meaningful destinations; test the blue-gray separately if it becomes a link or small caption.'
      }
    ]
  },
  {
    slug: 'neutral',
    title: 'Neutral Color Palettes',
    description: 'Warm, cool and monochrome neutral palettes with HEX codes, plus a catalog example that fixes faint secondary text and provides checked CSS roles.',
    answer: 'A neutral color palette centers on white, gray, black, beige or brown, often with a subtle warm or cool undertone. It still needs clear differences between surfaces, text and interactive elements. These three schemes assign those roles explicitly so you can compare a warm beige, cool gray and monochrome starting point.',
    updated: '2026-09-14',
    tips: [
      'Choose the warm or cool undertone beside your actual photos; surrounding colors change how a neutral appears.',
      'Separate hierarchy with spacing and type as well as color. Similar grays alone may be hard to distinguish.',
      'Give links an underline and give selected controls a visible shape or marker, especially in monochrome interfaces.'
    ],
    palettes: [
      {
        name: 'Warm linen and espresso',
        colors: 'F8F5EF-DFD5C6-B7A795-78614F-302A25',
        surface: '#F8F5EF',
        text: '#302A25',
        accent: '#78614F',
        note: 'Build the page on linen with espresso text, then use the medium brown for a primary action. Beige and taupe can divide generous content areas without adding a new hue. For a furniture catalog or text-led portfolio, compare these backgrounds beside the images first: warm neutrals can make cool product photos feel more pronounced.'
      },
      {
        name: 'Cool mist and graphite',
        colors: 'F4F6F8-DCE2E8-A8B3C0-506176-252D38',
        surface: '#F4F6F8',
        text: '#252D38',
        accent: '#506176',
        note: 'Mist gives tables and reading areas a light base, graphite supplies body text and slate blue-gray marks the main action. The middle grays work as supporting fills. For a documentation or dashboard layout, keep secondary labels readable instead of simply making them very pale, and check input borders as a separate pair.'
      },
      {
        name: 'Paper and charcoal',
        colors: 'FAFAFA-E5E5E5-A3A3A3-525252-1F1F1F',
        surface: '#FAFAFA',
        text: '#1F1F1F',
        accent: '#525252',
        note: 'Use paper for the main surface, charcoal for reading and the darker middle gray for actions. Light gray can fill a sidebar or a quiet decorative panel. This monochrome scheme keeps the focus on content, but states need more than shade changes: include labels, underlines or icons when communicating a selected option or a status.'
      }
    ]
  },
  {
    slug: 'dark-mode',
    title: 'Dark Mode Color Palettes',
    description: 'Dark mode palettes with navy, charcoal and plum HEX codes, plus a dashboard example with checked text, action labels, and both sides of a card edge.',
    answer: 'A dark mode color palette combines a dark surface, a light reading color and accents chosen for that background. Reassign colors by role rather than simply inverting a light theme. These three examples provide a main surface, a supporting dark swatch and a tested light text pair, with the accent label checked separately.',
    updated: '2026-09-14',
    tips: [
      'Check each elevated card against its own text and controls; a pass on the page background does not transfer automatically.',
      'Reserve brighter accents for a small number of actions. Use spacing and headings to organize the rest of the screen.',
      'Check focus rings, hover states, charts and images in the complete dark interface before shipping the theme.'
    ],
    palettes: [
      {
        name: 'Navy and glacier',
        colors: '111827-1F2937-E5E7EB-93C5FD-A7F3D0',
        surface: '#111827',
        text: '#E5E7EB',
        accent: '#93C5FD',
        note: 'Use deep navy for the page and light gray for reading. Glacier blue fills the main action, while the second dark swatch can distinguish a card or sidebar. Mint is a supporting accent for illustrations; if it communicates success, add a text label or icon and verify the actual state component independently.'
      },
      {
        name: 'Charcoal and amber',
        colors: '191919-292929-F2EDE4-F2BE63-B7C9A8',
        surface: '#191919',
        text: '#F2EDE4',
        accent: '#F2BE63',
        note: 'Warm off-white text softens the visual temperature of a charcoal surface. Amber gives the main action a clear focal point, while sage supports smaller decorative areas. For a media or reading interface, preview a full paragraph alongside artwork: do not assume that passing the text ratio settles image brightness or the visibility of player controls.'
      },
      {
        name: 'Plum and lavender',
        colors: '211B2D-32283F-F0EAF6-C4A7E7-F2B5C4',
        surface: '#211B2D',
        text: '#F0EAF6',
        accent: '#C4A7E7',
        note: 'A dark plum surface and pale lilac text create a purple-led theme without placing saturated purple behind long passages. Lavender fills the action and rose provides an optional secondary highlight. Use the lighter plum swatch for supporting panels, then recheck any text moved onto it; the main surface result only describes the pair shown here.'
      }
    ]
  }
];
